import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { v2_permissions } from "../actions/v2/rbac";
import constant from "../constant";

const PERM_CACHE_KEY = "vi_permissions_cache";

const PermissionContext = createContext();

const hasAuthSession = () =>
  sessionStorage.getItem(constant.keys.authSession) === "1";

const readPermCache = () => {
  try {
    const raw = sessionStorage.getItem(PERM_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.permissions) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const clearPermissionCache = () => {
  sessionStorage.removeItem(PERM_CACHE_KEY);
};

export const PermissionProvider = ({ children }) => {
  const cached = readPermCache();
  const [permissions, setPermissions] = useState(cached?.permissions || {});
  const [roleName, setRoleName] = useState(cached?.role_name || null);
  const [loading, setLoading] = useState(() => hasAuthSession() && !cached);

  const loadPermissions = async ({ background = false } = {}) => {
    if (!hasAuthSession()) {
      setPermissions({});
      setRoleName(null);
      setLoading(false);
      return;
    }

    if (!background && !readPermCache()) {
      setLoading(true);
    }

    try {
      const res = await v2_permissions();

      if (res.status && res.result) {
        const { role_name, permissions: perms } = res.result;
        setRoleName(role_name || null);
        setPermissions(perms || {});
        sessionStorage.setItem(
          PERM_CACHE_KEY,
          JSON.stringify({ role_name, permissions: perms || {} })
        );
      } else {
        if (!readPermCache()) {
          setPermissions({});
          setRoleName(null);
        }
      }
    } catch (err) {
      console.error("Error loading permissions:", err);
      if (!readPermCache()) {
        setPermissions({});
        setRoleName(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAuthSession()) {
      setLoading(false);
      return;
    }
    loadPermissions({ background: Boolean(cached) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      permissions,
      roleName,
      loading,
      reloadPermissions: () => loadPermissions({ background: false }),
    }),
    [permissions, roleName, loading]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);
